import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useIsomorphicLayoutEffect } from 'react-use';

import { hydrateSelectedItemsAction, resetSelectedItemsAction, selectedItemsSelector } from '@air/redux-selected-items';

import { GalleryItemType } from '~/components/Gallery/types';
import { useWorkspaceAgentExpandedAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentExpandedAssetView';
import { useUploadedBatchOfferStore } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';
import { type AirState } from '~/store/types';

/**
 * While the agent panel is open over the asset modal, mirror the viewed clip into workspace
 * selection so the composer scope bar and send metadata match what the user is looking at.
 */
export const usePrivateWorkspaceAgentAssetViewSelectionSync = () => {
  const dispatch = useDispatch();
  const store = useStore<AirState>();
  const { clipId, isExpandedAssetViewModalSession, retainExpandedAssetViewComposerSession } =
    useWorkspaceAgentExpandedAssetView();
  const clip = useSelector(centralizedClipSelector);
  const selectedItems = useSelector(selectedItemsSelector);
  const isComposerAssetViewScopeOptedOut = useWorkspaceAgentPanelStore(
    (state) => state.isComposerAssetViewScopeOptedOut,
  );
  const resetComposerAssetViewScopeOptOut = useWorkspaceAgentPanelStore(
    (state) => state.resetComposerAssetViewScopeOptOut,
  );
  const hasAttachedUploadedBatch = useUploadedBatchOfferStore((state) => state.attachedBatch !== null);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  const syncedClipIdRef = useRef<string | null>(null);
  const wasModalSessionActiveRef = useRef<boolean>(false);

  const shouldHydrate =
    retainExpandedAssetViewComposerSession &&
    !isComposerAssetViewScopeOptedOut &&
    // The thread reset re-arms the opt-out, so an attached batch must hold off the viewed clip itself.
    !hasAttachedUploadedBatch &&
    Boolean(clipId) &&
    clip?.id === clipId;

  useEffect(() => {
    const enteredModalSession = isExpandedAssetViewModalSession && !wasModalSessionActiveRef.current;
    wasModalSessionActiveRef.current = isExpandedAssetViewModalSession;

    if (clipId || enteredModalSession) {
      resetComposerAssetViewScopeOptOut();
    }
  }, [clipId, isExpandedAssetViewModalSession, resetComposerAssetViewScopeOptOut]);

  useIsomorphicLayoutEffect(() => {
    if (!shouldHydrate || !clip?.id) {
      return;
    }

    const selectionIsCurrent =
      Boolean(clipId) &&
      clip.id === clipId &&
      selectedItems.length === 1 &&
      selectedItems[0].type === GalleryItemType.asset &&
      selectedItems[0].id === clipId;

    if (selectionIsCurrent) {
      syncedClipIdRef.current = clip.id;

      return;
    }

    if (selectAll) {
      resetSelectAll();
    }

    dispatch(
      hydrateSelectedItemsAction({
        selectedItems: [{ id: clip.id, item: clip, type: GalleryItemType.asset }],
      }),
    );
    syncedClipIdRef.current = clip.id;
  }, [clip, clipId, dispatch, resetSelectAll, selectAll, selectedItems, shouldHydrate]);

  useEffect(() => {
    if (isExpandedAssetViewModalSession) {
      return;
    }

    const syncedClipId = syncedClipIdRef.current;
    syncedClipIdRef.current = null;

    if (syncedClipId !== null) {
      const items = selectedItemsSelector(store.getState());

      if (items.length === 1 && items[0].type === GalleryItemType.asset && items[0].id === syncedClipId) {
        dispatch(resetSelectedItemsAction());
      }
    }

    resetComposerAssetViewScopeOptOut();
  }, [dispatch, isExpandedAssetViewModalSession, resetComposerAssetViewScopeOptOut, store]);
};
